import type { APIAthleteProfileCareerStats, APIAthleteProfileClips, APIAthleteProfileSportAttributes } from '@eden';
import type { PlayerID, VideoClipID, UserID, AthleteProfileID } from '@common/ids';
import { toPlayerID, toVideoClipID } from '@common/ids';
import type { PlayerStatus, Player, PlayerAttributes, PublicPlayer } from '@common/player';
import type { Sport } from '@common/sport';
import type { UserPlayerAssociation, TeamGroup, TeamMemberUser } from '@common/user';
import { buildPlayerDisplayName, buildShortPlayerDisplayName } from '@util/buildPlayerDisplayName';

export type { UserPlayerAssociation, TeamGroup, PlayerStatus, TeamMemberUser, Player, PlayerAttributes, PublicPlayer };
export type { PlayerID, VideoClipID };
export { toPlayerID, toVideoClipID };
export const NAMESPACE = 'teammember';

export const isTeamMember = (maybeTeamMember: any): maybeTeamMember is TeamMemberUser => {
    return maybeTeamMember?.userID != undefined && maybeTeamMember?.isLoggedInUser != undefined;
};

export const isPlayer = (maybePlayer: any): maybePlayer is Player => {
    return maybePlayer?.id != undefined && maybePlayer?.playerAttributes != undefined;
};

export const teamMemberIsPlayer = (maybeTeamMember: any) => {
    if (!isTeamMember(maybeTeamMember)) return false;
    const { teamGroups = [] } = maybeTeamMember;
    return teamGroups.includes('player');
};

export const teamMemberIsStaffOrFamily = (maybeTeamMember: any) => {
    if (!isTeamMember(maybeTeamMember)) return false;
    const { teamGroups = [] } = maybeTeamMember;
    return teamGroups.includes('family') || teamGroups.includes('manager');
};

export const playerDisplayName = (player: Player, variant: 'normal' | 'short' = 'normal') => {
    switch (variant) {
        case 'normal':
            return buildPlayerDisplayName(
                player.firstName,
                player.lastName,
                player.playerAttributes.jerseyNumber,
                player.email,
            );
        case 'short':
            return buildShortPlayerDisplayName(
                player.firstName,
                player.lastName,
                player.playerAttributes.jerseyNumber,
                player.email,
            );
    }
};

type RolloverAssociation = {
    firstName: string;
    lastName: string;
};

export type UserRolloverAssociation = RolloverAssociation & {
    id: UserID;
    email?: string;
};

export type PlayerRolloverAssocation = RolloverAssociation & {
    id: PlayerID;
    number: string;
};

export type PublishStatus = 'published' | 'unpublished';

export interface AthleteProfile {
    id: AthleteProfileID;
    firstName: string;
    lastName: string;
    avatarUrl?: string;
    bio: string | null;
    graduationYear: string | null;
    sportAttributes: APIAthleteProfileSportAttributes | null;
    publishStatus: PublishStatus;
    clips: APIAthleteProfileClips;
    missingPremiumSubscription: boolean;
    title: string;
    sport?: Sport | null;
    careerStats: APIAthleteProfileCareerStats | null;
    loadAthleteProfileAuthenticatedClipUrl: Function;
}
